import { FOLDER_COLORS, type FolderColor } from '@regia/shared';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from './cn';

export function folderStyle(color: FolderColor): CSSProperties {
  const c = FOLDER_COLORS[color] ?? FOLDER_COLORS.grafite;
  return { '--folder': c.bg, '--folder-fg': c.fg } as CSSProperties;
}

/** Nome valido per le View Transitions (gli id ULID iniziano con una cifra). */
export const folderTransitionName = (id: string) => `f-${id}`;

const ALIGN = ['justify-start', 'justify-center', 'justify-end'] as const;

export interface FolderItem {
  id: string;
  name: string;
  color: FolderColor;
  href: string;
  /** Contenuto della parte visibile della cartellina (badge, canali). */
  meta?: ReactNode;
  logoUrl?: string | null;
}

/** La linguetta da sola: per anteprime e scelte di colore. */
export function FolderTab({
  color,
  children,
  className,
}: {
  color: FolderColor;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn('folder-tab', className)} style={folderStyle(color)}>
      {children}
    </span>
  );
}

/**
 * La pila di cartelline: la cosa memorabile di Regia (spec 6). Le linguette sono sfalsate
 * come nelle vere cartelline d'archivio; ogni cartellina copre il fondo di quella prima.
 */
export function FolderStack({ items, className }: { items: FolderItem[]; className?: string }) {
  return (
    <ol className={cn('flex flex-col', className)}>
      {items.map((item, index) => (
        <li
          key={item.id}
          className={cn('relative', index > 0 && '-mt-12')}
          style={{ zIndex: index + 1 }}
        >
          <Link
            to={item.href}
            viewTransition
            className="group block rounded-[18px] focus-visible:outline-offset-4"
            style={
              {
                ...folderStyle(item.color),
                viewTransitionName: folderTransitionName(item.id),
              } as CSSProperties
            }
          >
            <span className={cn('flex px-4', ALIGN[index % 3])}>
              <span className="folder-tab transition-transform duration-200 group-hover:-translate-y-0.5">
                <span className="truncate">{item.name}</span>
              </span>
            </span>
            <span className="folder-body">
              <span className="flex min-h-7 flex-wrap items-center gap-1.5">{item.meta}</span>
              {item.logoUrl ? (
                <img
                  src={item.logoUrl}
                  alt=""
                  className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 object-cover ring-2 ring-white/40"
                />
              ) : null}
            </span>
          </Link>
        </li>
      ))}
    </ol>
  );
}

/** Badge da usare dentro la cartellina, leggibile su qualsiasi colore. */
export function FolderBadge({ children, tone }: { children: ReactNode; tone?: 'warn' | 'alert' }) {
  return (
    <span
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[0.8125rem] font-semibold whitespace-nowrap',
        tone === 'alert'
          ? 'bg-white text-[#B3142A]'
          : tone === 'warn'
            ? 'bg-[color-mix(in_srgb,var(--folder-fg)_92%,transparent)] text-[var(--folder)]'
            : 'bg-[color-mix(in_srgb,var(--folder-fg)_16%,transparent)] text-[var(--folder-fg)]',
      )}
    >
      {children}
    </span>
  );
}
